import { getIsContentStudentRestricted } from "@/assignments/utils/assignmentUtils";
import { shouldDisplayAdvisoryModal } from "@/assignments/utils/usePublishAssignment/usePublishAssignment";
import ArticleLevel from "@/types/models/ArticleLevel.interface";
import { Assignment } from "@/types/models/Assignment.interface";
import AssignButton from "@/view/AssignButton";
import contentTypes from "@/view/constants/contentTypes";
import {
  ASSIGNMENT_MODE,
  currentAssignmentMode,
} from "@/view/utils/currentAssignmentMode";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import { AssignModalProps } from "@/view/utils/useAssignmentReducerContext";
import { GenerationGeniusContent } from "@/view/GeniusContent/GeniusContent.interface";
import AssignActionsDropdown from "./AssignActionsDropdown";
import { buttonContainerCss } from "./AssignAction.style";

interface AssignActionInterface {
  content:
    | {
        content_type: keyof typeof contentTypes;
        data: {
          article_levels: ArticleLevel[];
        };
        id: string;
      }
    | GenerationGeniusContent;
  currentLevelId?: string;
  handlePublish: () => void;
  isMobile?: boolean;
  isSaving: boolean;
  setAdvisoryModalActive: (a: boolean) => void;
  setIsOpeningAssignmentSettings: (a: boolean) => void;
  originalAssignment: Assignment;
  triggerRef: React.Ref<HTMLButtonElement> | null;
  showMobileButton?: boolean | undefined;
}

const AssignAction = ({
  content,
  currentLevelId,
  handlePublish,
  isMobile,
  isSaving,
  originalAssignment,
  setAdvisoryModalActive,
  setIsOpeningAssignmentSettings,
  triggerRef,
  showMobileButton = false,
}: AssignActionInterface) => {
  const {
    assignModal: { setIsAssignModalActive },
    updatedAssignment,
  } = useAssignmentReducerContext() as {
    assignModal: AssignModalProps;
    updatedAssignment: Assignment;
  };

  const isStudentRestrictedContent = getIsContentStudentRestricted(content);

  const assignmentMode = currentAssignmentMode(updatedAssignment);
  const isUpdateMode = assignmentMode === ASSIGNMENT_MODE.UPDATE;

  const handleCreateAssignmentClick = (
    shouldOpenAssignmentSettings = false,
  ) => {
    /** Used to determne if Assignment Settings, rather than Save Changes was selected,
     * and what the following behavior should be
     * */
    setIsOpeningAssignmentSettings(shouldOpenAssignmentSettings);

    if (
      shouldDisplayAdvisoryModal(updatedAssignment, isStudentRestrictedContent)
    ) {
      setAdvisoryModalActive(true);
    } else {
      shouldOpenAssignmentSettings || !isUpdateMode
        ? setIsAssignModalActive(true)
        : handlePublish();
    }
  };

  const assignmentHasContentId = !!updatedAssignment?.content_id;

  const streamProperties = {
    content_id: currentLevelId || content.id,
    content_type: content?.content_type,
    ...(assignmentHasContentId
      ? { object_id: updatedAssignment.content_id }
      : {}),
  };

  return (
    <>
      <div css={buttonContainerCss(assignmentHasContentId)}>
        <AssignButton
          contentType={content?.content_type}
          isMobile={isMobile}
          isSaving={isSaving}
          onClick={() => handleCreateAssignmentClick()}
          triggerRef={triggerRef}
          streamProperties={streamProperties}
          updatedAssignment={updatedAssignment}
          showMobileButton={showMobileButton}
        />
        {assignmentHasContentId && !showMobileButton ? (
          <AssignActionsDropdown
            handleAssignmentSettingsClick={() =>
              handleCreateAssignmentClick(true)
            }
            isMobile={isMobile}
            originalAssignment={originalAssignment}
            streamProperties={streamProperties}
            updatedAssignment={updatedAssignment}
          />
        ) : null}
      </div>
    </>
  );
};

export default AssignAction;
